import { ApiError, ApiRequestOptions, ApiResult } from '../types/Api';

export const API_URL = import.meta.env.VITE_API_URL || '';
export const API_PATH = '/api/v1';

// Add a function for navigating programmatically
export const navigateToLogin = () => {
  window.location.href = '/login';
};

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
  noRedirect?: boolean;
  formData?: boolean;
}

/**
 * Generic API client function that handles auth tokens and errors
 */
export const apiClient = async <T>(
  endpoint: string, 
  options: RequestOptions = {}
): Promise<T> => {
  const { requiresAuth = false, headers = {}, formData = false, ...rest } = options;
  
  // Get auth token from localStorage if request requires authentication
  const token = requiresAuth ? localStorage.getItem('access_token') : null;
  
  // Prepare request headers
  const requestHeaders: Record<string, string> = {
    ...(headers as Record<string, string>),
  };
  
  // Add auth header if token exists
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  // Add Content-Type header for JSON requests if not already set and not a FormData request
  if (!requestHeaders['Content-Type'] && !(options.body instanceof FormData) && !formData) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  // Prepare full URL
  const url = `${API_URL}${API_PATH}${endpoint}`;

  // Create request options for error handling
  const requestOptions: ApiRequestOptions = {
    url,
    method: (rest.method as ApiRequestOptions['method']) || 'GET',
    headers: requestHeaders,
    body: options.body
  };

  // Make the fetch request
  const response = await fetch(url, {
    ...rest,
    headers: requestHeaders,
  });
  
  // For successful responses with no content
  if (response.status === 204) {
    return null as T;
  }
  
  // For other error responses
  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = await response.text();
    }
    
    // Create ApiResult for error
    const apiResult: ApiResult = {
      url,
      status: response.status,
      statusText: response.statusText,
      body: errorBody,
      ok: false
    };
    
    throw new ApiError(
      requestOptions,
      apiResult,
      response.statusText || 'API Error'
    );
  }
  
  // Parse JSON response (wrapped in try-catch for proxy/network corruption)
  try {
    const data = await response.json();
    return data as T;
  } catch {
    // If the response body can't be parsed as JSON (e.g. empty body, proxy corruption),
    // return null — callers expecting void/null will be fine
    return null as T;
  }
};

/**
 * Helper methods for common API operations
 */
export const apiGet = <T>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
  return apiClient<T>(endpoint, { ...options, requiresAuth: true });
};

export const apiPost = <T>(
  endpoint: string, 
  data: unknown, 
  options: RequestOptions = {}
): Promise<T> => {
  let processedBody: string | FormData;
  
  // Convert to FormData if formData flag is true and data is not already FormData
  if (options.formData && !(data instanceof FormData)) {
    const formData = new FormData();
    if (data && typeof data === 'object') {
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value as string | Blob);
        }
      });
    }
    processedBody = formData;
  } else if (data instanceof FormData) {
    processedBody = data;
  } else {
    // JSON stringify if not FormData
    processedBody = JSON.stringify(data);
  }
  
  return apiClient<T>(endpoint, {
    method: 'POST',
    body: processedBody,
    formData: options.formData,
    requiresAuth: true,
    ...options,
  });
};

export const apiPut = <T>(
  endpoint: string, 
  data: unknown, 
  options: RequestOptions = {}
): Promise<T> => {
  let processedBody: string | FormData;
  
  // Convert to FormData if formData flag is true and data is not already FormData
  if (options.formData && !(data instanceof FormData)) {
    const formData = new FormData();
    if (data && typeof data === 'object') {
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value as string | Blob);
        }
      });
    }
    processedBody = formData;
  } else if (data instanceof FormData) {
    processedBody = data;
  } else {
    // JSON stringify if not FormData
    processedBody = JSON.stringify(data);
  }

  return apiClient<T>(endpoint, {
    method: 'PUT',
    body: processedBody,
    formData: options.formData,
    requiresAuth: true,
    ...options,
  });
};

export const apiDelete = <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> => {
  return apiClient<T>(endpoint, {
    method: 'DELETE',
    requiresAuth: true,
    ...options,
  });
};

export const apiPatch = <T>(
  endpoint: string, 
  data: unknown, 
  options: RequestOptions = {}
): Promise<T> => {
  let processedBody: string | FormData;
  
  // Convert to FormData if formData flag is true and data is not already FormData
  if (options.formData && !(data instanceof FormData)) {
    const formData = new FormData();
    if (data && typeof data === 'object') {
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value as string | Blob);
        }
      });
    }
    processedBody = formData;
  } else if (data instanceof FormData) {
    processedBody = data;
  } else {
    // JSON stringify if not FormData
    processedBody = JSON.stringify(data);
  }

  return apiClient<T>(endpoint, {
    method: 'PATCH',
    body: processedBody,
    formData: options.formData,
    requiresAuth: true,
    ...options,
  });
};

/**
 * Download a file from the API and return as blob URL for display or download
 */
export const apiDownload = async (
  endpoint: string, 
  options: { 
    download?: boolean; 
    filename?: string;
    requiresAuth?: boolean;
  } = {}
): Promise<string> => {
  const { download = false, filename, requiresAuth = true } = options;
  
  // Get auth token if request requires authentication
  const token = requiresAuth ? localStorage.getItem('access_token') : null;
  
  // Prepare request headers
  const requestHeaders: Record<string, string> = {};
  
  // Add auth header if token exists
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Prepare full URL
  const url = `${API_URL}${API_PATH}${endpoint}`;

  // Make the fetch request
  const response = await fetch(url, {
    method: 'GET',
    headers: requestHeaders,
  });
  
  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = await response.text();
    }
    
    throw new ApiError(
      { url, method: 'GET', headers: requestHeaders },
      { url, status: response.status, statusText: response.statusText, body: errorBody, ok: false },
      response.statusText || 'Download Error'
    );
  }
  
  // Get the blob
  const blob = await response.blob();

  if (download) {
    // Extract filename from Content-Disposition header if not provided
    let downloadFilename = filename;
    if (!downloadFilename) {
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          downloadFilename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
    }

    // Trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadFilename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    return downloadUrl; // Return the URL even though we downloaded
  } else {
    // Return blob URL for display
    return window.URL.createObjectURL(blob);
  }
}; 